// React
import React, { useState, useMemo } from "react";
import { Add } from "@mui/icons-material";
import { Box, Button } from "@mui/material";
import { withTranslation } from "react-i18next";
import { useTranslation } from "react-i18next";
import { push } from "connected-react-router";

// Redux
import {
  useSpecialAssignments,
  useSpecialAssignmentsReport
} from "../../hooks/assignments";
import { useDispatch } from "react-redux";

// Custom
import AdminPageLayout from "../../components/FullPageLayout/AdminPageLayout";
import Table from "../../components/ReactTable/Table";
import CourseTypeIcon from "../../components/Icons/CourseTypeIcon";
import ButtonGroup from "../../components/Buttons/ButtonGroup";
import SpecialAssignmentsFilters from "./SpecialAssignmentsFilters";
import TrainingObjectsPickerModal from "./TrainingObjectsPickerModal";
import AddNewSpecialAssignmentModal from "./AddNewSpecialAssignmentModal";
import { buildSpecialAssignmentSettingsRoute } from "../../routes/AdminRoutes";
import { useTableData } from "../../components/ReactTable/useTableData";
import TextFilter from "../../components/ReactTable/Filters/TextFilter";
import DateFilter from "../../components/ReactTable/Filters/DateFilter";

const columns = t => [
  {
    Header: t("table.columnHeader.type"),
    accessor: "type",
    id: "type",
    canSort: true,
    Cell: ({ cell: { value } }) => <CourseTypeIcon type={value} />
  },
  {
    Header: t("table.columnHeader.name"),
    accessor: "name",
    id: "name",
    canSort: true,
    Filter: TextFilter,
    filterPlaceholder: t("table.filterPlaceholder.nameAll")
  },
  {
    Header: t("table.columnHeader.DueDate"),
    accessor: "dueDate",
    id: "due_date",
    canSort: true,
    Filter: DateFilter,
    filterPlaceholder: t("table.filterPlaceholder.dateAll")
  },
  {
    Header: t("table.columnHeader.assigned"),
    accessor: "assigned",
    id: "assigned",
    canSort: true,
    Filter: TextFilter,
    filterPlaceholder: t("table.filterPlaceholder.Employee")
  },
  {
    Header: t("table.columnHeader.completed"),
    accessor: "completed",
    id: "completed",
    canSort: true,
    Filter: TextFilter,
    filterPlaceholder: t("table.filterPlaceholder.Employee")
  }
];

const SpecialAssignments = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [trainingObjectsModalOpen, setTrainingObjectsModalOpen] = useState(
    false
  );
  const [selectedTrainingObject, setSelectedTrainingObject] = useState(null);
  const report = useSpecialAssignmentsReport();

  const memoColumns = useMemo(() => columns(t), []);
  const {
    getSpecialAssignments,
    createNewSpecialAssignment,
    isSubmitting,
    isFetching
  } = useSpecialAssignments();

  const handleRowClick = assignmentId => {
    dispatch(push(buildSpecialAssignmentSettingsRoute(assignmentId)));
  };

  const handleCreateAssignment = assignmentData => {
    createNewSpecialAssignment({
      trainingObjectId: assignmentData.id,
      name: assignmentData.name,
      dueDate: assignmentData.dueDate
    }).then(newAssignment => {
      setSelectedTrainingObject(null);
      setTrainingObjectsModalOpen(false);
      refreshData();
      handleRowClick(newAssignment.id);
    });
  };

  const {
    data,
    refreshData,
    paginationState,
    onStateChange,
    onApplyExternalFilters
  } = useTableData(getSpecialAssignments);

  const filter = (e, { setSubmitting }) => {
    onApplyExternalFilters(e);
    setSubmitting(false);
  };

  return (
    <AdminPageLayout
      title={t("specialAssignments.title")}
      headerActions={
        <ButtonGroup align="left" flexWrap="wrap">
          <Button color="primary" variant="outlined" onClick={report}>
            {t("specialAssignments.Individual/SpecialAssignment")}
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Add />}
            onClick={() => setTrainingObjectsModalOpen(true)}
            data-qa="add-course-btn"
          >
            {t("specialAssignments.addSpecialAssignment")}
          </Button>
        </ButtonGroup>
      }
    >
      <Box mb={3}>
        <SpecialAssignmentsFilters onSubmit={filter} />
      </Box>
      <Table
        onStateChange={onStateChange}
        paginationState={paginationState}
        paginated
        cellSize="medium"
        columns={memoColumns}
        data={data}
        onRowClick={rowData => handleRowClick(rowData.original.id)}
        loading={isFetching}
      />
      <TrainingObjectsPickerModal
        open={trainingObjectsModalOpen}
        onClose={() => setTrainingObjectsModalOpen(false)}
        onSelectTrainingObject={setSelectedTrainingObject}
      />
      <AddNewSpecialAssignmentModal
        open={!!selectedTrainingObject}
        onClose={() => setSelectedTrainingObject(null)}
        onCreateAssignment={handleCreateAssignment}
        courseDetails={selectedTrainingObject}
        disabled={isSubmitting}
      />
    </AdminPageLayout>
  );
};

const TranslatedSpecialAssignments = withTranslation()(SpecialAssignments);

export default TranslatedSpecialAssignments;
